<template>
  <div class="addon-manage">
    <div class="toolbar">
      <el-input
        v-model.trim="filters.keyword"
        class="keyword-input"
        clearable
        prefix-icon="el-icon-search"
        placeholder="赛事、运动员、手机号或订单号"
        @clear="fetchData(true)"
        @keyup.enter.native="fetchData(true)"
      ></el-input>
      <el-input
        v-model.trim="filters.eventId"
        class="event-input"
        clearable
        placeholder="赛事ID"
        @clear="fetchData(true)"
        @keyup.enter.native="fetchData(true)"
      ></el-input>
      <el-select
        v-model="filters.profitSharingStatus"
        class="status-select"
        clearable
        placeholder="全部分账状态"
        @change="fetchData(true)"
      >
        <el-option label="无需分账" value="none"></el-option>
        <el-option label="待分账" value="pending"></el-option>
        <el-option label="已受理" value="submitted"></el-option>
        <el-option label="分账成功" value="success"></el-option>
        <el-option label="分账失败" value="failed"></el-option>
      </el-select>
      <el-button
        type="primary"
        icon="el-icon-search"
        :loading="loading"
        @click="fetchData(true)"
      >查询</el-button>
      <el-button icon="el-icon-refresh" :loading="loading" @click="resetFilters">重置</el-button>
      <el-button
        v-if="canExport"
        type="primary"
        plain
        icon="el-icon-download"
        :loading="exporting"
        :disabled="!total"
        @click="exportRows"
      >导出</el-button>
    </div>

    <div class="list-summary">
      <span>仅展示支付成功且已完成项目合并的兼项订单</span>
      <el-tag size="small" type="success">成功订单 {{ total }} 笔</el-tag>
    </div>

    <el-table v-loading="loading" :data="rows" border>
      <el-table-column prop="eventName" label="赛事名称" min-width="260" show-overflow-tooltip></el-table-column>
      <el-table-column label="运动员" width="120">
        <template slot-scope="scope">
          <div>{{ scope.row.athleteName || '--' }}</div>
          <div class="muted">{{ scope.row.athletePhone || '--' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="追加项目" min-width="220">
        <template slot-scope="scope">
          <div v-for="(item, index) in scope.row.items || []" :key="index">
            {{ item.item }}{{ item.group }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="businessOrderNo" label="追加单号" min-width="225" show-overflow-tooltip></el-table-column>
      <el-table-column prop="transactionId" label="微信交易号" min-width="245" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.transactionId || '--' }}</template>
      </el-table-column>
      <el-table-column label="实付金额" width="105" align="right">
        <template slot-scope="scope">{{ formatAmount(scope.row.paymentAmount || scope.row.payableAmount) }} 元</template>
      </el-table-column>
      <el-table-column label="订单状态" width="95" align="center">
        <template>
          <el-tag size="mini" type="success">已完成</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="平台分账金额" width="125" align="right">
        <template slot-scope="scope">{{ formatAmount(scope.row.platformFee) }} 元</template>
      </el-table-column>
      <el-table-column label="分账状态" width="105" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="profitSharingTagType(scope.row)">
            {{ profitSharingStatusText(scope.row) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="profitSharingNo" label="分账单号" min-width="225" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.profitSharingNo || '--' }}</template>
      </el-table-column>
      <el-table-column label="支付时间" width="170">
        <template slot-scope="scope">{{ formatTime(scope.row.payTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="145" fixed="right" align="center">
        <template slot-scope="scope">
          <el-button
            type="text"
            size="small"
            :disabled="!scope.row.profitSharingResult"
            @click="showProfitSharingResult(scope.row.profitSharingResult)"
          >分账响应</el-button>
          <el-button
            v-if="canRetryProfitSharing"
            type="text"
            size="small"
            :loading="retryingOrderNo === scope.row.businessOrderNo"
            :disabled="!canRetry(scope.row)"
            @click="retryProfitSharing(scope.row)"
          >重试</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :page-sizes="[20, 50, 100]"
      :page-size="pageSize"
      :current-page="pageNum"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    ></el-pagination>

    <el-dialog
      title="分账响应详情"
      :visible.sync="resultDialogVisible"
      :width="isMobile ? '92%' : '760px'"
    >
      <pre class="json-viewer">{{ profitSharingResultText }}</pre>
      <span slot="footer">
        <el-button @click="resultDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getToken, hasPermission } from '@/utils/auth';
import { exportAddonManagementRows } from '@/utils/registrationExport';

export default {
  name: 'RegistrationAddonManage',
  data() {
    return {
      loading: false,
      exporting: false,
      rows: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      filters: {
        keyword: '',
        eventId: '',
        profitSharingStatus: ''
      },
      retryingOrderNo: '',
      resultDialogVisible: false,
      profitSharingResultText: '',
      isMobile: false
    };
  },
  computed: {
    canExport() {
      return hasPermission('registration:export');
    },
    canRetryProfitSharing() {
      return hasPermission('registration:profitSharing');
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  methods: {
    authHeaders() {
      return { 'x-auth-token': getToken() };
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    buildParams(extra = {}) {
      const params = {
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        ...extra
      };
      if (this.filters.keyword) params.keyword = this.filters.keyword;
      if (this.filters.eventId) params.eventId = this.filters.eventId;
      if (this.filters.profitSharingStatus) {
        params.profitSharingStatus = this.filters.profitSharingStatus;
      }
      return params;
    },
    fetchData(resetPage = false) {
      if (resetPage) this.pageNum = 1;
      this.loading = true;
      this.$axios.get('/form-result/find_addons_page', {
        headers: this.authHeaders(),
        params: this.buildParams()
      }).then((res) => {
        const result = (res.data && res.data.res) || {};
        this.rows = result.list || [];
        this.total = result.total || 0;
        this.pageNum = result.pageNum || this.pageNum;
        this.pageSize = result.pageSize || this.pageSize;
      }).catch((err) => {
        const message = err.response && err.response.data && err.response.data.message;
        this.$message.error(message || '兼项订单加载失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        eventId: '',
        profitSharingStatus: ''
      };
      this.fetchData(true);
    },
    handlePageChange(page) {
      this.pageNum = page;
      this.fetchData();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.fetchData(true);
    },
    formatAmount(value) {
      return (Number(value || 0) / 100).toFixed(2).replace(/\.?0+$/, '');
    },
    formatTime(value) {
      return value ? this.$dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--';
    },
    profitSharingStatusText(row) {
      if (!row.isProfitSharing) return '无需分账';
      return {
        none: '无需分账',
        pending: '待分账',
        submitted: '已受理',
        success: '成功',
        failed: '失败'
      }[row.profitSharingStatus] || '未知';
    },
    profitSharingTagType(row) {
      if (!row.isProfitSharing || row.profitSharingStatus === 'none') return 'info';
      return {
        pending: 'warning',
        submitted: 'warning',
        success: 'success',
        failed: 'danger'
      }[row.profitSharingStatus] || 'info';
    },
    showProfitSharingResult(result) {
      if (!result) return;
      this.profitSharingResultText = JSON.stringify(result, null, 2);
      this.resultDialogVisible = true;
    },
    canRetry(row) {
      return Boolean(
        row &&
        row.status === 'paid' &&
        row.paymentMode === 'service_provider' &&
        row.isProfitSharing &&
        ['pending', 'failed', 'submitted'].includes(row.profitSharingStatus)
      );
    },
    retryProfitSharing(row) {
      if (!this.canRetry(row)) return;
      this.$confirm(
        `确认将追加单 ${row.businessOrderNo} 加入分账重试队列？`,
        '重试分账确认',
        {
          confirmButtonText: '确认重试',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.retryingOrderNo = row.businessOrderNo;
        return this.$axios.post('/form-result/retry_addon_profit_sharing', {
          businessOrderNo: row.businessOrderNo
        }, {
          headers: this.authHeaders()
        });
      }).then(() => {
        this.$message.success('已加入分账重试队列');
        this.fetchData();
      }).catch((err) => {
        if (err === 'cancel' || err === 'close') return;
        const message = err.response && err.response.data && err.response.data.message;
        this.$message.error(message || '加入分账重试队列失败');
      }).finally(() => {
        this.retryingOrderNo = '';
      });
    },
    exportRows() {
      this.exporting = true;
      this.$axios.get('/form-result/find_addons_page', {
        headers: this.authHeaders(),
        params: this.buildParams({
          exportAll: true,
          pageNum: 1,
          pageSize: 5000
        })
      }).then((res) => {
        const result = (res.data && res.data.res) || {};
        const rows = result.list || [];
        if (!rows.length) {
          this.$message.warning('暂无可导出的兼项订单');
          return;
        }
        exportAddonManagementRows(rows, this.$dayjs);
        if (result.total > rows.length) {
          this.$message.warning(`订单超过 5000 笔，本次已导出前 ${rows.length} 笔`);
        } else {
          this.$message.success(`已导出 ${rows.length} 笔兼项订单`);
        }
      }).catch((err) => {
        const message = err.response && err.response.data && err.response.data.message;
        this.$message.error(message || '导出失败');
      }).finally(() => {
        this.exporting = false;
      });
    }
  }
};
</script>

<style scoped>
.addon-manage {
  width: 100%;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.keyword-input {
  width: 320px;
}

.event-input {
  width: 220px;
}

.status-select {
  width: 160px;
}

.list-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 10px 14px;
  color: var(--text-regular);
  background: var(--bg-table-header);
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  font-size: 13px;
}

.muted {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 12px;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.json-viewer {
  max-height: 520px;
  margin: 0;
  padding: 16px;
  overflow: auto;
  color: var(--text-primary);
  background: var(--bg-page);
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

@media (max-width: 768px) {
  .keyword-input,
  .event-input,
  .status-select {
    width: 100%;
  }

  .list-summary {
    align-items: flex-start;
    gap: 8px;
  }
}
</style>
